import React, { useContext, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { InformationMessage } from "nemuru-components";
import IconTitle from "../../../../../shared/components/IconTitle";
import MainButton from "../../../../../shared/components/MainButton";
import Text from "../../../../../shared/components/Text";
import { DocumentIdUploadCard } from "./DocumentIdUploadCard";
import { useDocumentIdPictureUploadFlow } from "../hooks/useDocumentIdPictureUploadFlow";
import { useDocumentIdPictureStepErrors } from "../hooks/useDocumentIdPictureStepErrors";
import { ProcessContext } from "../../../../../../contexts/ProcessContext";
import { getDocumentIdDocumentLabel } from "../../../config";
import { useTranslation } from "../../../../../../utils/hooks/useTranslation";

const EMPTY_UPLOAD_STATE = {
  hasPicture: false,
  capturedBase64: undefined,
  fileName: undefined,
  fileError: null,
};

export const DocumentIdPictureUpload = ({ onSubmitForm }) => {
  const { palette } = useTheme();
  const {
    state: { participant, idDocumentVerification },
    dispatch,
  } = useContext(ProcessContext);
  const { getTextIdTranslation } = useTranslation();

  const onboardingDetailsId = participant.onboardingDetails?.id;
  const documentType = idDocumentVerification.documentType;

  const isNie = documentType === "nie_eu" || documentType === "nie_non_eu";
  const frontTitleTid = isNie
    ? "LOAD_DOCUMENT_ID_FRONT_NIE"
    : "LOAD_DOCUMENT_ID_FRONT_DNI";
  const backTitleTid = isNie
    ? "LOAD_DOCUMENT_ID_BACK_NIE"
    : "LOAD_DOCUMENT_ID_BACK_DNI";

  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [frontState, setFrontState] = useState(EMPTY_UPLOAD_STATE);
  const [backState, setBackState] = useState(EMPTY_UPLOAD_STATE);

  const bothUploaded = frontState.hasPicture && backState.hasPicture;

  const {
    uploadingFront,
    uploadingBack,
    isVerifying,
    failedSide,
    handleContinue,
    handleRetry,
  } = useDocumentIdPictureUploadFlow({
    dispatch,
    onSubmitForm,
    onboardingDetailsId,
    idDocumentVerification,
    frontBase64: frontState.capturedBase64,
    backBase64: backState.capturedBase64,
    frontRef,
    backRef,
  });

  const isLoading = uploadingFront || uploadingBack || isVerifying;

  const {
    invalidPhotoError,
    processExpiredError,
    genericError,
    retriableVerifyError,
    hasError,
  } = useDocumentIdPictureStepErrors({
    idDocumentVerification,
    isUploadMode: true,
    hasSelectedPicture: bothUploaded,
    selectedBase64: frontState.capturedBase64,
  });

  const showErrorBanner = hasError && !isVerifying;
  const canRenderCards = !isLoading && !showErrorBanner;

  const loadingTid = uploadingFront
    ? "UPLOADING_FRONT_PICTURE"
    : uploadingBack
    ? "UPLOADING_BACK_PICTURE"
    : "VERIFYING_DOCUMENT";

  const disableAction = isLoading || !bothUploaded || hasError;

  const retryUploadAfterError = () => {
    setFrontState(EMPTY_UPLOAD_STATE);
    setBackState(EMPTY_UPLOAD_STATE);
    handleRetry();
  };

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          title={
            <Text
              tId="DOCUMENT_ID_VERIFICATION"
              dynamicDataTexts={[getDocumentIdDocumentLabel(documentType)]}
            />
          }
          subtitle={<Text tId="UPLOAD_DOCUMENT_ID_PICTURES" />}
        />
      </Box>
      <Box>
        {isLoading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <CircularProgress color="primary" />
            <Box mt={2}>
              <Typography
                align="center"
                variant="body2"
                style={{ color: palette.text.title }}
              >
                <Text tId={loadingTid} />
              </Typography>
            </Box>
          </Box>
        )}
        {canRenderCards && (
          <Box display="flex" flexDirection="column" gridGap={8}>
            <DocumentIdUploadCard
              ref={frontRef}
              documentSide="front"
              titleTid={frontTitleTid}
              onStateChange={setFrontState}
            />
            <DocumentIdUploadCard
              ref={backRef}
              documentSide="back"
              titleTid={backTitleTid}
              onStateChange={setBackState}
            />
            <Box display="flex" gridGap={4} alignItems="center">
              <i
                className="material-icons"
                style={{
                  fontSize: "24px",
                  color: palette.primary.main,
                  lineHeight: "24px",
                }}
              >
                {"horizontal_rule"}
              </i>
              <Typography
                style={{ color: palette.text.title }}
                variant="caption"
              >
                <Text tId={"PROCESS_INFO_BULLET_HORIZONTAL"} />
              </Typography>
            </Box>
          </Box>
        )}
        {showErrorBanner && processExpiredError && (
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  timer_off
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: <Text tId="ID_DOC_UPLOAD_PROCESS_EXPIRED_TITLE" />,
              body: <Text tId="ID_DOC_UPLOAD_PROCESS_EXPIRED_BODY" />,
            }}
          />
        )}
        {showErrorBanner && invalidPhotoError && (
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  error_outline
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: (
                <Text
                  tId="ID_DOC_UPLOAD_INVALID_PHOTO_TITLE"
                  dynamicDataTexts={[getTextIdTranslation(failedSide)]}
                />
              ),
              body: <Text tId="ID_DOC_UPLOAD_INVALID_PHOTO_BODY" />,
            }}
          />
        )}
        {showErrorBanner && genericError && (
          <InformationMessage
            variant={{
              type: "error",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 22 }}>
                  error_outline
                </i>
              ),
            }}
            isScreenXs={false}
            messageContent={{
              title: <Text tId="ID_DOC_UPLOAD_GENERIC_ERROR_TITLE" />,
              body: (
                <Text
                  tId={
                    retriableVerifyError
                      ? "ID_DOC_VERIFY_GENERIC_ERROR_BODY"
                      : "ID_DOC_UPLOAD_GENERIC_ERROR_BODY"
                  }
                />
              ),
            }}
          />
        )}
      </Box>
      <Box height={10} />
      <Box>
        {showErrorBanner && processExpiredError ? (
          <MainButton onClick={retryUploadAfterError}>
            <Text tId="RESTART_PROCESS" />
          </MainButton>
        ) : showErrorBanner ? (
          <Box display="flex" flexDirection="column">
            <MainButton onClick={retryUploadAfterError}>
              <Text tId="RETRY" />
            </MainButton>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column">
            <MainButton onClick={handleContinue} disabled={disableAction}>
              <Text tId="UPLOAD_PICTURES_ACTION" />
            </MainButton>
          </Box>
        )}
      </Box>
    </>
  );
};
